// src/EnhancedComponents/Sidebar.js
import React from "react";
import { NavLink } from "react-router-dom";
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";

export const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: <DashboardRoundedIcon />, end: true },
  { to: "/users", label: "Users", icon: <PeopleAltRoundedIcon /> },
  { to: "/events", label: "Events", icon: <EventRoundedIcon /> },
];

const itemSx = (collapsed) => ({
  minHeight: 46,
  borderRadius: 2,
  mb: 0.5,
  px: collapsed ? 1.5 : 2,
  justifyContent: collapsed ? "center" : "flex-start",
  color: "sidebar.text",
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: collapsed ? 0 : 40,
  },
  "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" },
  "&.active": {
    bgcolor: "sidebar.active",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(30,136,229,0.35)",
  },
  "&.Mui-focusVisible": { outline: "2px solid #fff", outlineOffset: -2 },
});

export default function Sidebar({ collapsed = false, onLogout }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "sidebar.bg",
        color: "sidebar.text",
        overflowX: "hidden",
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: collapsed ? 1.5 : 2.5,
          py: 2.5,
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <Box
          component="img"
          src="/assets/img/Logo_Bhopal_Jamaat.png"
          alt="Bhopal Jamaat"
          sx={{
            width: 44,
            height: 44,
            objectFit: "contain",
            bgcolor: "#fff",
            borderRadius: 2,
            p: 0.5,
            flexShrink: 0,
          }}
        />
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" color="#fff" noWrap lineHeight={1.2}>
              Bhopal Jamaat
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }} noWrap>
              Admin Panel
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* Navigation */}
      <Box component="nav" aria-label="Main navigation" sx={{ flex: 1, px: 1.5, py: 2 }}>
        {!collapsed && (
          <Typography
            variant="overline"
            sx={{ px: 1, opacity: 0.6, display: "block", lineHeight: 2.5 }}
          >
            Menu
          </Typography>
        )}
        <List disablePadding>
          {NAV_ITEMS.map((item) => (
            <Tooltip
              key={item.to}
              title={collapsed ? item.label : ""}
              placement="right"
              disableHoverListener={!collapsed}
              disableFocusListener={!collapsed}
            >
              <ListItemButton
                component={NavLink}
                to={item.to}
                end={item.end}
                aria-label={collapsed ? item.label : undefined}
                sx={itemSx(collapsed)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    slotProps={{ primary: { fontWeight: 500 } }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
      </Box>

      {/* Logout */}
      <Box sx={{ p: 1.5 }}>
        <Tooltip
          title={collapsed ? "Logout" : ""}
          placement="right"
          disableHoverListener={!collapsed}
          disableFocusListener={!collapsed}
        >
          <ListItemButton
            onClick={onLogout}
            aria-label={collapsed ? "Logout" : undefined}
            sx={{
              ...itemSx(collapsed),
              mb: 0,
              "&:hover": { bgcolor: "rgba(239,83,80,0.15)", color: "#ff8a80" },
            }}
          >
            <ListItemIcon>
              <LogoutRoundedIcon />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Logout" />}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
